/**
 * BESTIX v5.0 — CheckoutPage
 * Stripe-integrated checkout flow for ticket purchasing
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useCartStore, selectCartItems, selectCartSubtotalCents, selectIsCartEmpty } from '@/stores/cart.store';
import { useAuthStore, selectIsAuthenticated, selectUser } from '@/stores/auth.store';
import { createPaymentIntent, completeBooking, type CustomerInfo } from '@/services/eventsApi';

// ============================================
// Types
// ============================================

interface CheckoutFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  street1: string;
  street2: string;
  city: string;
  state: string;
  zipCode: string;
}

type CheckoutStep = 'review' | 'payment' | 'processing';

// ============================================
// Price Formatting
// ============================================

function formatPrice(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
}

// ============================================
// Checkout Form Component
// ============================================

export default function CheckoutPage() {
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  
  // Store state
  const cartItems = useCartStore(selectCartItems);
  const subtotalCents = useCartStore(selectCartSubtotalCents);
  const isCartEmpty = useCartStore(selectIsCartEmpty);
  const clearCart = useCartStore((state) => state.clearCart);
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const user = useAuthStore(selectUser);
  
  // Local state
  const [step, setStep] = useState<CheckoutStep>('review');
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [lockId, setLockId] = useState<string | null>(null);
  const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [lockExpiresAt, setLockExpiresAt] = useState<string | null>(null);
  
  const [formData, setFormData] = useState<CheckoutFormData>({
    firstName: '',
    lastName: '',
    email: user?.email || '',
    phone: '',
    street1: '',
    street2: '',
    city: '',
    state: '',
    zipCode: '',
  });

  // Calculate fees (15% service fee)
  const serviceFeePercent = 0.15;
  const serviceFeeCents = Math.round(subtotalCents * serviceFeePercent);
  const totalCents = subtotalCents + serviceFeeCents;

  // Update email when user changes
  useEffect(() => {
    if (user?.email) {
      setFormData(prev => ({ ...prev, email: user.email }));
    }
  }, [user?.email]);

  // Redirect if cart is empty
  useEffect(() => {
    if (isCartEmpty) {
      navigate('/events');
    }
  }, [isCartEmpty, navigate]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Step 1: Create payment intent and lock tickets
  const handleProceedToPayment = async () => {
    if (cartItems.length === 0) return;
    
    setIsProcessing(true);
    setError(null);
    
    try {
      // For now, we only support single ticket group checkout
      const item = cartItems[0];
      
      const response = await createPaymentIntent(
        item.ticketGroupId,
        item.quantity,
        item.eventId,
        formData.email || undefined
      );
      
      setClientSecret(response.client_secret);
      setLockId(response.lock_id);
      setPaymentIntentId(response.payment_intent_id);
      setLockExpiresAt(response.lock_expires_at || null);
      setStep('payment');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to lock tickets. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Step 2: Process payment with Stripe
  const handleSubmitPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!stripe || !elements || !clientSecret || !lockId || !paymentIntentId) {
      setError('Payment system not ready. Please refresh and try again.');
      return;
    }

    // Validate form
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone ||
        !formData.street1 || !formData.city || !formData.state || !formData.zipCode) {
      setError('Please fill in all required fields.');
      return;
    }

    // Check if user is authenticated (required for booking)
    if (!isAuthenticated) {
      setError('Please sign in to complete your purchase.');
      return;
    }

    setIsProcessing(true);
    setStep('processing');
    setError(null);

    try {
      // Confirm payment with Stripe
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        throw new Error('Card element not found');
      }

      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: `${formData.firstName} ${formData.lastName}`,
            email: formData.email,
            phone: formData.phone,
            address: {
              line1: formData.street1,
              line2: formData.street2 || undefined,
              city: formData.city,
              state: formData.state,
              postal_code: formData.zipCode,
              country: 'US',
            },
          },
        },
      });

      if (stripeError) {
        throw new Error(stripeError.message);
      }

      if (paymentIntent?.status !== 'succeeded') {
        throw new Error(`Payment not completed: ${paymentIntent?.status}`);
      }

      // Complete booking with backend
      const customer: CustomerInfo = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        address: {
          street1: formData.street1,
          street2: formData.street2 || undefined,
          city: formData.city,
          state: formData.state,
          zipCode: formData.zipCode,
          country: 'US',
        },
      };

      const bookingResponse = await completeBooking(
        lockId,
        paymentIntentId,
        customer,
        'E-Ticket'
      );

      // Clear cart and redirect to success
      clearCart();
      navigate('/checkout/success', {
        state: {
          orderId: bookingResponse.order_id,
          mercuryTransactionId: bookingResponse.mercury_transaction_id,
          totalAmount: bookingResponse.total_amount,
        },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Payment failed. Please try again.');
      setStep('payment');
    } finally {
      setIsProcessing(false);
    }
  };

  // Lock expiration timer
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  
  useEffect(() => {
    if (!lockExpiresAt) return;
    
    const updateTimer = () => {
      const now = Date.now();
      const expires = new Date(lockExpiresAt).getTime();
      const remaining = Math.max(0, Math.floor((expires - now) / 1000));
      setTimeRemaining(remaining);
      
      if (remaining <= 0) {
        setError('Ticket lock expired. Please start over.');
        setStep('review');
        setClientSecret(null);
        setLockId(null);
      }
    };
    
    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [lockExpiresAt]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <>
      <Helmet><title>Checkout | BESTIX</title></Helmet>
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <Link to="/" className="font-playfair text-2xl font-bold text-navy">BESTIX</Link>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                Secure Checkout
              </div>
            </div>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="bg-white border-b">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center justify-center gap-4">
              {['Review', 'Payment', 'Confirm'].map((label, index) => {
                const stepIndex = step === 'review' ? 0 : step === 'payment' ? 1 : 2;
                return (
                  <div key={label} className="flex items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      stepIndex > index ? 'bg-green-500 text-white' :
                      stepIndex === index ? 'bg-navy text-white' : 'bg-gray-200 text-gray-500'
                    }`}>
                      {stepIndex > index ? '✓' : index + 1}
                    </div>
                    <span className={`ml-2 text-sm ${stepIndex === index ? 'text-navy font-medium' : 'text-gray-500'}`}>
                      {label}
                    </span>
                    {index < 2 && <div className="w-12 h-px bg-gray-200 mx-4" />}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Lock Timer */}
        {step === 'payment' && timeRemaining !== null && (
          <div className={`py-2 text-center text-sm font-medium ${
            timeRemaining < 60 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
          }`}>
            Tickets reserved for {formatTime(timeRemaining)}
          </div>
        )}

        {/* Error Banner */}
        {error && (
          <div className="bg-red-50 border-b border-red-200 py-3">
            <div className="container mx-auto px-4">
              <p className="text-red-700 text-center">{error}</p>
            </div>
          </div>
        )}

        <div className="container mx-auto px-4 py-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Order Review */}
              <div className="bg-white rounded-xl shadow-md p-6">
                <h2 className="text-xl font-semibold text-navy mb-4">Order Review</h2>
                {cartItems.map((item) => (
                  <div key={item.id} className="flex gap-4 py-4 border-b last:border-0">
                    <div className="w-20 h-20 bg-gradient-to-br from-navy to-navy-dark rounded-lg flex items-center justify-center overflow-hidden">
                      {item.event.imageUrl ? (
                        <img src={item.event.imageUrl} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <svg className="w-8 h-8 text-gold/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-navy">{item.event.name}</h3>
                      <p className="text-sm text-gray-600">{item.event.venue}</p>
                      <p className="text-sm text-gray-500">{item.event.date} • {item.event.time}</p>
                      <p className="text-sm text-gray-500">
                        Section {item.ticket.section} • Row {item.ticket.row}
                        {item.ticket.seats && ` • Seats ${item.ticket.seats}`}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-navy">{formatPrice(item.ticket.priceCents * item.quantity)}</p>
                      <p className="text-sm text-gray-500">{item.quantity} × {formatPrice(item.ticket.priceCents)}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Customer Information */}
              <div className="bg-white rounded-xl shadow-md p-6">
                <h2 className="text-xl font-semibold text-navy mb-4">Contact Information</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+1-555-123-4567"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
                      required
                    />
                  </div>
                </div>

                <h3 className="text-lg font-medium text-navy mt-6 mb-4">Billing Address</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Street Address *</label>
                    <input
                      type="text"
                      name="street1"
                      value={formData.street1}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Apt, Suite, etc.</label>
                    <input
                      type="text"
                      name="street2"
                      value={formData.street2}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
                    />
                  </div>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">State *</label>
                      <input
                        type="text"
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                        placeholder="CA"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">ZIP Code *</label>
                      <input
                        type="text"
                        name="zipCode"
                        value={formData.zipCode}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment - Only show after proceeding */}
              {step === 'payment' && (
                <div className="bg-white rounded-xl shadow-md p-6">
                  <h2 className="text-xl font-semibold text-navy mb-4">Payment</h2>
                  <form onSubmit={handleSubmitPayment}>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Card Details</label>
                      <div className="p-4 border border-gray-300 rounded-lg">
                        <CardElement
                          options={{
                            style: {
                              base: {
                                fontSize: '16px',
                                color: '#0a2540',
                                fontFamily: 'Inter, system-ui, sans-serif',
                                '::placeholder': {
                                  color: '#9ca3af',
                                },
                              },
                              invalid: {
                                color: '#dc2626',
                              },
                            },
                          }}
                        />
                      </div>
                    </div>
                    
                    {!isAuthenticated && (
                      <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                        <p className="text-amber-700 text-sm">
                          Please <Link to="/signin" className="font-medium underline">sign in</Link> to complete your purchase.
                        </p>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={isProcessing || !stripe || !isAuthenticated}
                      className="w-full btn btn-primary py-4 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isProcessing ? 'Processing...' : `Pay ${formatPrice(totalCents)}`}
                    </button>
                  </form>
                </div>
              )}
            </div>

            {/* Order Summary Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-md p-6 sticky top-4">
                <h2 className="text-xl font-semibold text-navy mb-4">Order Summary</h2>
                
                <div className="space-y-3 pb-4 border-b">
                  <div className="flex justify-between text-gray-600">
                    <span>Tickets ({cartItems.reduce((sum, i) => sum + i.quantity, 0)})</span>
                    <span>{formatPrice(subtotalCents)}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>Service Fee</span>
                    <span>{formatPrice(serviceFeeCents)}</span>
                  </div>
                </div>

                <div className="flex justify-between py-4 text-lg font-bold text-navy">
                  <span>Total</span>
                  <span>{formatPrice(totalCents)}</span>
                </div>

                {step === 'review' && (
                  <button
                    onClick={handleProceedToPayment}
                    disabled={isProcessing || cartItems.length === 0}
                    className="block w-full btn btn-primary py-4 text-center text-lg disabled:opacity-50"
                  >
                    {isProcessing ? 'Locking Tickets...' : 'Proceed to Payment'}
                  </button>
                )}

                <div className="mt-4 flex items-center justify-center gap-2 text-sm text-gray-500">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  <span>Protected by Buyer Guarantee</span>
                </div>

                <p className="text-xs text-gray-400 text-center mt-4">
                  By completing this purchase, you agree to our{' '}
                  <Link to="/terms" className="underline">Terms</Link> and{' '}
                  <Link to="/refunds" className="underline">Refund Policy</Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
